import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { getUnixTime, fromUnixTime, format } from 'date-fns';
import {
  Spinner,
  DateRangePicker,
  Button,
  Popover,
  PopoverButton,
  StyledPopoverPanel,
  PopoverItem,
  LoaderBalloon,
} from '@crazyegginc/hatch';

import { v4 as uuid } from 'uuid';

import { ReactComponent as DownloadIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-download.svg';

import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { SitePickerBar } from '/src/components/site-picker/index';
import { NoMatchesWall } from '../../_global/paywalls/NoMatchesWall';
import { NotInstalledWall } from '../walls/NotInstalled';
import { NoSourcesWall } from '../walls/NoSources';
import { columnOrderAtom, getDefaultOrder, lastValidOrderAtom, searchAtom } from '../components/table';
import { DetailView } from './detail';
import { ListView } from './list';

import {
  webAnalyticsOverTimeQuery,
  webAnalyticsQuery,
  webAnalyticsConversionsOverTimeQuery,
  webAnalyticsDimensionQuery,
} from '../queries';
import {
  useAuthContext,
  useQueryParams,
  usePermissions,
  useNotifications,
  useHasFeatureFlag,
  useModal,
  useTimeZonedFunctions,
  useSite,
} from '/src/hooks';

import { FEATURES, SPECIAL_DATE_RANGES, TIMEZONES } from '/src/features/_global/constants';
import { WEB_ANALYTIC_METRICS, VALID_DIMENSION_VALUES, OTHERS } from '../constants';

import { parseDateRange } from '../utils';
import { getFeatureName } from '/src/features/_global/utils';
import { toAtom } from '/src/utils/string';
import { ensureValidSearch } from '/src/utils/url';
import { scrollToLater } from '/src/utils/scroll';
import { bearerAuthHeader } from '/src/utils/auth';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { OptInWall } from '../walls/OptIn';
import { selectedGoalAtom } from '../components/goal-selector';
import { AskOwnerWall } from '../walls/AskOwner';
import { SOURCE_TYPE, SourceSelector, useSelectedSource } from '/src/components/source-selector/source-selector';
import { useSources } from '/src/hooks/useSources';
import { STATUS, usePageParams } from '/src/hooks/usePageParams';
import { ManageGaPropertiesModal } from '../../google-analytics/modals/ManageGaProperties';
import { DateRange } from '/src/components/date-time/DateRange';
import { meQuery } from '../../_global/queries';
import { DashboardBanner } from '/src/components/DashboardBanner';
import { SupportLinks } from '/src/support';
import {
  FIRST_VISIT_MODAL_FLOW,
  GA_FIRST_VISIT_TYPE,
  InstructionalModal,
} from '../components/modals/InstructionalModal';
import { SupportLink } from '/src/features/_global/components/SupportLink';

const MAX_DAYS = 365;

// eslint-disable-next-line no-restricted-syntax
export default function WebAnalyticsDashboard() {
  useSite();
  const modal = useModal();
  const { allSelectableSources, loading: loadingSources, selectedSource } = useSources();
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.WEB_ANALYTICS);
  const { getDefaultDateRange } = useTimeZonedFunctions(selectedSource?.currentTimezone ?? TIMEZONES.UTC);

  const getDefaultDateRangeParam = useCallback(() => {
    const range = getDefaultDateRange({
      featureStartedAt: selectedSource?.webAnalyticsEnabledAt ?? selectedSource?.createdAt,
    });

    if (range?.special) return range.special;

    if (range?.start_date && range?.end_date) {
      return `${getUnixTime(range.start_date)}-${getUnixTime(range.end_date)}`;
    }

    return SPECIAL_DATE_RANGES.LAST_7_DAYS;
  }, [getDefaultDateRange, selectedSource?.webAnalyticsEnabledAt, selectedSource?.createdAt]);

  const pageParamsConfig = useMemo(
    () => [
      { key: 'date_range', defaultValue: getDefaultDateRangeParam, sync: true },
      { key: 'metric', defaultValue: WEB_ANALYTIC_METRICS.UNIQUE_VISITORS, sync: true },
      { key: 'site' },
      { key: 'property' },
      { key: 'modal' },
    ],
    [getDefaultDateRangeParam],
  );

  const { status, params: pageParams } = usePageParams(pageParamsConfig);

  useEffect(() => {
    if (pageParams?.modal && pageParams.modal === 'manage_ga') {
      modal.show(<ManageGaPropertiesModal />);
    }
  }, [pageParams?.modal, modal]);

  const hasSource = Array.isArray(selectedSource) ? selectedSource.length > 0 : Boolean(selectedSource);

  const isFirstSource = !loadingSources && !allSelectableSources.length;

  if (!canViewDashboard) {
    return <DashboardPaywall reason={reason} feature={FEATURES.WEB_ANALYTICS} />;
  }

  const sourcesLoading = Boolean(selectedSource && loadingSources === false);

  return (
    <DashboardPage>
      <SEO title="Web Analytics" />
      <DashHeader titleComponent={getFeatureName(FEATURES.WEB_ANALYTICS)} />
      <PaymentAlerts />
      {isFirstSource ? (
        <div className="p-5">
          <NoSourcesWall />
        </div>
      ) : status === STATUS.READY && hasSource && !sourcesLoading ? (
        <WebAnalyticsContent />
      ) : (
        <div className="mt-16 flex w-full items-center justify-center">
          <Spinner />
          <div className="ml-2.5">Loading...</div>
        </div>
      )}
    </DashboardPage>
  );
}

function WebAnalyticsContent() {
  const { currentAccount } = useAuthContext();
  const permissions = usePermissions();
  const { sites, gaProperties, loading } = useSources();
  const { selectedSource } = useSelectedSource();
  const modal = useModal();

  const hasRestrictedSites = (currentAccount.restrictedSitesIds || []).length > 0;

  const canSetUpAnalytics = permissions.can('edit', FEATURES.WEB_ANALYTICS).allowed;
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  useEffect(() => {
    if (loading || !selectedSource) {
      return;
    }

    const getStorageFlag = (key) => {
      try {
        return localStorage.getItem(key) === 'true';
      } catch {
        return false;
      }
    };

    const setStorageFlag = (key) => {
      try {
        localStorage.setItem(key, 'true');
      } catch {
        // no-op when localStorage is unavailable
      }
    };

    const isGaPropertySource = selectedSource.type === SOURCE_TYPE.GA_PROPERTY;
    const hasEnabledSuccessfullyConnectedGAProperty =
      currentAccount?.gaProperties?.some((prop) => !!prop.enabledAt && !!prop.lastSuccessAt) ?? false;

    const modalConditions = {
      [GA_FIRST_VISIT_TYPE.GA_INTRO]: () => !isGaPropertySource && (currentAccount?.gaProperties?.length ?? 0) === 0,
      [GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER]: () =>
        (currentAccount?.gaProperties?.length ?? 0) > 0 && hasEnabledSuccessfullyConnectedGAProperty,
    };

    const showModalFromIndex = (index = 0) => {
      if (index >= FIRST_VISIT_MODAL_FLOW.length) {
        return;
      }

      const flowItem = FIRST_VISIT_MODAL_FLOW[index];
      const hasSeenModal = getStorageFlag(flowItem.storageKey);
      const shouldShowModal = modalConditions[flowItem.type]?.() ?? true;

      if (hasSeenModal || !shouldShowModal) {
        showModalFromIndex(index + 1);
        return;
      }

      modal.show(
        <InstructionalModal
          type={flowItem.type}
          onClose={() => {
            setStorageFlag(flowItem.storageKey);
            showModalFromIndex(index + 1);
          }}
          onConfirm={() => {
            if ([GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER, GA_FIRST_VISIT_TYPE.GA_INTRO].includes(flowItem.type)) {
              window.dispatchEvent(new CustomEvent('openSourcePicker'));
            }
          }}
        />,
      );
    };

    showModalFromIndex();
  }, [selectedSource, modal, loading, currentAccount]);

  if (!selectedSource) return null;

  return (
    <>
      <SitePickerBar
        Picker={SourceSelector}
        sites={sites}
        gaProperties={hasRestrictedSites ? [] : gaProperties}
        loading={loading}
        addSiteOption={canEditSites}
        enableGoogleAnalytics={!hasRestrictedSites}
        pageDisplayName="Web Analytics dashboard"
      />
      <GoogleAnalyticsSyncAlert />
      <div className="flex flex-col p-10 !pt-5">
        {selectedSource && selectedSource.type === SOURCE_TYPE.SITE ? (
          selectedSource && !selectedSource.installed ? (
            <NotInstalledWall />
          ) : selectedSource && !selectedSource.webAnalyticsEnabledAt ? (
            canSetUpAnalytics ? (
              <OptInWall />
            ) : (
              <AskOwnerWall />
            )
          ) : (
            <RenderAnalytics />
          )
        ) : selectedSource && selectedSource.type === SOURCE_TYPE.GA_PROPERTY ? (
          <RenderAnalytics />
        ) : null}
      </div>
    </>
  );
}

function GoogleAnalyticsSyncAlert() {
  const { selectedSource } = useSelectedSource();

  const [hasDismissedAlerts, setHasDismissedAlerts] = useState(
    () => window.sessionStorage.getItem('ce.dashboards.analytics.dismissedAlerts') ?? false,
  );

  function dismissAlerts() {
    window.sessionStorage.setItem('ce.dashboards.analytics.dismissedAlerts', 'true');
    setHasDismissedAlerts(true);
  }

  const hasError = !!selectedSource.lastError;

  if (hasDismissedAlerts || !hasError) {
    return null;
  }

  return (
    <DashboardBanner
      onClose={dismissAlerts}
      type="warning"
      title="We ran into a problem syncing data from Google Analytics into Crazy Egg"
    >
      We’re on it and will update your Web Analytics dashboard as soon as the data comes in. If the problem persists,{' '}
      <SupportLink className="text-link !text-white !underline" href={SupportLinks.general.newSupportRequest}>
        contact Support for help.
      </SupportLink>
    </DashboardBanner>
  );
}

const DEFAULT_LIMIT = 250;

function parseOrderDir(view, desc) {
  if (!view) return undefined;
  if (desc === true) return 'DESC';
  if (desc === false) return 'ASC';
  return undefined;
}

function parseOrderBy(view, field) {
  if (!view) return undefined;
  return toAtom(field);
}

// Only run queries in this component, we use the ones above
// to ensure the params are set and the site is loaded.
function RenderAnalytics() {
  const intervalRef = useRef(null);
  const takingLongRef = useRef(null);
  const permissions = usePermissions();
  const { token } = useAuthContext();
  const location = useLocation();
  const hasOverTime = useHasFeatureFlag('analytics-over-time');
  const canExportJson = useHasFeatureFlag('web-analytics-json-export');
  const canExportCsv = permissions.can('csv', FEATURES.EXPORT).allowed;
  const isDashboard = location.pathname.endsWith('analytics');
  const [pollState, setPollState] = useState({ id: null, status: 'off' });
  const [timeUnitOverride] = useState(null);

  const { selectedSource } = useSelectedSource();
  const tz = useTimeZonedFunctions(selectedSource?.currentTimezone ?? TIMEZONES.UTC);

  // whether to show the export button
  const canExport = canExportJson || canExportCsv;

  const { get: paramGet, set: paramSet } = useQueryParams();
  const [order, setOrder] = useAtom(columnOrderAtom);
  const [selectedGoal] = useAtom(selectedGoalAtom);
  const [lastValidOrder, setLastValidOrder] = useAtom(lastValidOrderAtom);
  const [search, setSearch] = useAtom(searchAtom);
  const { view } = useParams();
  const navigate = useNavigate();
  const notifications = useNotifications();

  const pickerLowerBoundary =
    selectedSource?.type === SOURCE_TYPE.GA_PROPERTY
      ? tz.startOfDay(tz.subMonths(selectedSource.createdAt, 6))
      : tz.startOfDay(selectedSource.webAnalyticsEnabledAt);

  const pickerUpperBoundary = tz.endOfDay(new Date());

  const numSegments = Number(paramGet('num_segments', '10'));

  useEffect(() => {
    if (!view) return;
    if (!VALID_DIMENSION_VALUES.includes(view)) {
      notifications.error({
        id: 'invalid-dimension',
        content: 'Invalid dimension selected.',
        timeout: 2000,
        skipHoneybadger: true,
      });
      navigate(`/web-analytics${location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const dateRange = paramGet('date_range');
  const parsedDateRange = parseDateRange(dateRange, pickerLowerBoundary, pickerUpperBoundary, MAX_DAYS);
  const [startDate, endDate, predefinedRange] = parsedDateRange;

  const dateRangeConfig = useMemo(
    () => ({
      special: predefinedRange,
      start_date: startDate,
      end_date: endDate,
    }),
    [predefinedRange, startDate, endDate],
  );

  const tzRange = useMemo(() => tz.getConvertedDateRange(dateRangeConfig), [tz, dateRangeConfig]);

  useEffect(() => {
    if (startDate && endDate) {
      const [currStart, currEnd] = dateRange.split('-');
      if (Number(currStart) !== startDate || Number(currEnd) !== endDate) {
        paramSet('date_range', `${startDate}-${endDate}`);
      }
    }
  }, [paramSet, dateRange, startDate, endDate]);

  const orderColumnForOverTime = parseOrderBy(view, order?.id, false);

  const { refetch: fetchMe } = useQuery({
    ...meQuery(),
    enabled: false,
  });

  const pollPropertyReady = useCallback(async () => {
    if (!selectedSource?.id) return false;

    try {
      const res = await fetch(`${window.CORE_API_URL}/ga/${selectedSource.id}/check`, {
        headers: {
          ...bearerAuthHeader(token),
          'x-request-id': uuid(),
        },
      });
      if (!res.ok) return false;

      const text = (await res.text()).trim();
      if (!text) return false;

      if (text.toLowerCase() === 'ok') return true;

      try {
        const data = JSON.parse(text);
        const status = String(data?.status ?? data?.state ?? data?.result ?? '').toLowerCase();
        return status === 'ok';
      } catch {
        return false;
      }
    } catch {
      return false;
    }
  }, [selectedSource?.id, token]);

  const { data: rawOverTime, isLoading: overTimeLoading } = useQuery({
    ...webAnalyticsOverTimeQuery({
      dimension: view,
      metric: orderColumnForOverTime || lastValidOrder,
      sourceId: selectedSource?.id,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
      numSegments: numSegments,
      goalId: selectedGoal?.id,
    }),
    refetchOnMount: 'always',
    enabled: hasOverTime && Boolean(selectedSource) && Boolean(view) && !isDashboard,
  });

  const { data: rawConversions, isLoading: _conversionsLoading } = useQuery({
    ...webAnalyticsConversionsOverTimeQuery({
      goalId: selectedGoal?.id,
      sourceId: selectedSource?.id,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
    }),
    refetchOnMount: 'always',
    // enabled: hasOverTime && Boolean(selectedGoal?.id) && Boolean(selectedSource?.id) && isDashboard,
    enabled: false,
  });

  const canLoadData =
    (selectedSource?.type === SOURCE_TYPE.GA_PROPERTY && !!selectedSource?.lastSuccessAt) ||
    selectedSource?.type === SOURCE_TYPE.SITE;

  const {
    data: dashData,
    isLoading: dashLoading,
    hasNextPage: dashHasNextPage,
    fetchNextPage: dashFetchNextPage,
  } = useInfiniteQuery({
    ...webAnalyticsQuery({
      view: 'ALL',
      sourceId: selectedSource?.id,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
      orderBy: parseOrderBy(view, order?.id),
      orderDir: parseOrderDir(view, order?.desc),
      limit: view ? DEFAULT_LIMIT : 25,
      granularity: timeUnitOverride || tzRange.timeUnit,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: ['polling', 'stillPolling'].includes(pollState.status) ? 60000 : false,
    enabled:
      canLoadData &&
      isDashboard &&
      Boolean(selectedSource) &&
      ((Boolean(startDate) && Boolean(endDate)) || Boolean(predefinedRange)),
  });

  const {
    data: detailData,
    isLoading: detailLoading,
    hasNextPage: detailHasNextPage,
    fetchNextPage: detailFetchNextPage,
  } = useInfiniteQuery({
    ...webAnalyticsDimensionQuery({
      dimension: toAtom(view),
      sourceId: selectedSource?.id,
      sourceType: selectedSource?.type,
      startAt: tzRange.startDate,
      endAt: tzRange.endDate,
      searchTerm: search,
      orderBy: parseOrderBy(view, order?.id),
      orderDir: parseOrderDir(view, order?.desc),
      limit: view ? DEFAULT_LIMIT : 25,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: ['polling', 'stillPolling'].includes(pollState.status) ? 60000 : false,
    enabled:
      canLoadData &&
      !isDashboard &&
      Boolean(selectedSource) &&
      ((Boolean(startDate) && Boolean(endDate)) || Boolean(predefinedRange)),
  });

  const data = isDashboard ? dashData : detailData;
  const isLoading = isDashboard ? dashLoading : detailLoading;
  const hasNextPage = isDashboard ? dashHasNextPage : detailHasNextPage;
  const fetchNextPage = isDashboard ? dashFetchNextPage : detailFetchNextPage;

  const pageData = useMemo(() => {
    if (!data?.pages) return null;

    if (isDashboard) {
      return data.pages?.[0]?.analytics ?? {};
    }

    let accData = data.pages.reduce((acc, page) => [...acc, ...(page?.analyticsDimension?.dimension?.data ?? [])], []);

    const lastPage = data.pages.at(-1)?.analyticsDimension ?? {};

    return {
      nextCursor: lastPage.nextCursor,
      dimension: {
        data: accData,
        totalCount: lastPage.dimension.totalCount,
      },
    };
  }, [data, isDashboard]);

  const webAnalyticsData = useMemo(() => {
    return {
      ...pageData,
      dimension: {
        ...(pageData?.dimension ?? {}),
      },
    };
  }, [pageData]);

  const overTimeData = useMemo(() => {
    const data = rawOverTime?.analyticsOverTime?.[view]?.overTime ?? [];

    const reordered = data.slice().sort((a, b) => {
      const isAOthers = a.name === OTHERS;
      const isBOthers = b.name === OTHERS;

      if (isAOthers && !isBOthers) return 1;
      if (!isAOthers && isBOthers) return -1;
      if ((isAOthers && isBOthers) || (!isAOthers && !isBOthers)) return 0;
    });

    return reordered;
  }, [rawOverTime?.analyticsOverTime, view]);

  const conversionsData = useMemo(() => {
    if (!view || view === 'ALL') {
      return rawConversions?.analyticsConversionsOverTime ?? [];
    }
    return rawOverTime?.analyticsOverTime?.[view]?.conversions ?? [];
  }, [rawOverTime?.analyticsOverTime, rawConversions?.analyticsConversionsOverTime, view]);

  const chooseDateRange = useCallback(
    (value) => {
      if (value.special) {
        paramSet('date_range', value.special);
      } else if (value.start_date && value.end_date) {
        const newStartDate = getUnixTime(value.start_date);
        const newEndDate = getUnixTime(value.end_date);

        paramSet('date_range', `${newStartDate}-${newEndDate}`);
      }
    },
    [paramSet],
  );

  const hasData = Object.keys(webAnalyticsData || {}).length > 0;

  useEffect(() => {
    const isGA = selectedSource.type === SOURCE_TYPE.GA_PROPERTY;

    // if not GA, force OFF, but only if needed
    if (!isGA) {
      setPollState((prev) => {
        const next = { id: selectedSource?.id, status: 'off' };
        return prev.id === next.id && prev.status === next.status ? prev : next;
      });
      return;
    }

    // if GA changed, reset.
    if (pollState.id !== selectedSource.id) {
      setPollState({ id: selectedSource.id, status: 'off' });
      return;
    }

    if (!selectedSource.lastSuccessAt) {
      // keep polling
      if (pollState.status !== 'polling' && pollState.status !== 'stillPolling') {
        setPollState({ id: selectedSource.id, status: 'polling' });
      }
      return;
    }

    if (['polling', 'stillPolling'].includes(pollState.status)) {
      // success
      setPollState({ id: selectedSource.id, status: 'transition' });
    }
  }, [pollState, selectedSource]);

  useEffect(() => {
    if (pollState.status !== 'transition') return;

    const timeoutId = setTimeout(() => {
      setPollState({ id: selectedSource.id, status: 'off' });
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [pollState, selectedSource.id]);

  useEffect(() => {
    const isPolling = ['polling', 'stillPolling'].includes(pollState.status) && pollState.id === selectedSource.id;

    if (!isPolling) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (takingLongRef.current) {
        clearTimeout(takingLongRef.current);
        takingLongRef.current = null;
      }
      return;
    }

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = window.setInterval(async () => {
      const isReady = await pollPropertyReady();

      if (isReady) {
        await fetchMe();
      }
    }, 5000);

    if (pollState.status === 'polling') {
      if (takingLongRef.current) clearTimeout(takingLongRef.current);

      takingLongRef.current = window.setTimeout(() => {
        setPollState((prev) =>
          prev.id === selectedSource.id && prev.status === 'polling'
            ? { id: selectedSource.id, status: 'stillPolling' }
            : prev,
        );
      }, 60_000);
    }

    // cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (takingLongRef.current) {
        clearTimeout(takingLongRef.current);
        takingLongRef.current = null;
      }
    };
  }, [pollState.status, pollState.id, selectedSource.id, pollPropertyReady, fetchMe]);

  function downloadResults() {
    if (!canExportJson) return;

    if (!webAnalyticsData || Object.keys(webAnalyticsData).length === 0) {
      return;
    }

    try {
      const date = new Date().toISOString().slice(0, 10);

      const jsonString = JSON.stringify(webAnalyticsData, null, 2);
      const resultsBlob = new Blob([jsonString], { type: 'application/json' });
      downloadBlob(resultsBlob, `web-analytics-${date}.json`);
    } catch (error) {
      console.error('Failed to export analytics data:', error);
      notifications.error({ id: 'analyticsExportError', timeout: 3000, content: `Failed to export analytics data.` });
    }
  }

  const exportData = useCallback(async () => {
    if (!canExportCsv) return;

    const exportPath = location.pathname.replaceAll('-', '_');
    const ext = isDashboard ? 'xlsx' : 'csv';

    const searchParams = new URLSearchParams(location.search);

    const params = new URLSearchParams({
      source_id: String(selectedSource?.id ?? ''),
      source_type: String(selectedSource?.type ?? ''),
      token,
    });

    if (predefinedRange) {
      params.set('preset_date', predefinedRange);
    } else {
      const exportStart = tzRange?.startDate ?? startDate;
      const exportEnd = tzRange?.endDate ?? endDate;

      params.set('start_at', exportStart);
      params.set('end_at', exportEnd);
    }

    if (searchParams.get('metric')) params.set('metric', searchParams.get('metric'));
    if (searchParams.get('order')) params.set('order', searchParams.get('order'));
    if (searchParams.get('sort')) params.set('sort', searchParams.get('sort'));
    if (searchParams.get('search')) params.set('search', searchParams.get('search'));

    const [start, end] = [tzRange?.startDate ?? startDate, tzRange?.endDate ?? endDate].map((v) => {
      return format(fromUnixTime(v), 'yyyy-MM-dd');
    });

    const datePart = start !== end ? `${start}-to-${end}` : start;

    const res = await fetch(`${window.CORE_API_URL}/export${exportPath}.${ext}?${params.toString()}`);

    if (res.status === 200) {
      const workbookBlob = await res.blob();

      const filename = isDashboard ? `web-analytics-${datePart}.xlsx` : `${view}-${datePart}.csv`;
      downloadBlob(workbookBlob, filename);
    } else {
      notifications.error({
        content: 'Failed to export your analytics data.',
        context: { response: res },
      });
    }
  }, [
    view,
    startDate,
    endDate,
    tzRange,
    predefinedRange,
    location.pathname,
    location.search,
    selectedSource?.id,
    selectedSource?.type,
    isDashboard,
    canExportCsv,
    token,
    notifications,
  ]);

  const showGaLoader =
    selectedSource?.type === SOURCE_TYPE.GA_PROPERTY &&
    (['polling', 'stillPolling', 'transition'].includes(pollState.status) || !selectedSource.lastSuccessAt);

  return (
    <>
      <div className="mx-auto flex w-full min-w-[940px] flex-col ">
        {view ? (
          <div>
            <Button
              variant="cancel"
              onClick={() => {
                setSearch('');
                setOrder(getDefaultOrder());
                setLastValidOrder(getDefaultOrder().id);
                navigate({
                  pathname: '/web-analytics',
                  search: ensureValidSearch(location.search, ['source_id', 'source_type', 'date_range', 'metric']),
                });
              }}
              className="!px-1 flex items-center space-x-1"
            >
              <ArrowIcon className="rotate-180 w-2.5 h-2.5 fill-current" />
              <span>Back</span>
            </Button>
          </div>
        ) : null}

        <div className="mb-2.5 flex justify-between items-center">
          <div className="ml-1 font-semibold text-lg text-black-pearl-500">
            <DateRange dateRange={dateRangeConfig} timeZone={selectedSource?.currentTimezone} />
          </div>
          <div className="mr-1 flex items-center space-x-2">
            {canExport ? (
              <div className="flex justify-end w-full">
                <Popover className="relative" data-testid="question-menu">
                  {() => (
                    <>
                      <PopoverButton
                        as={Button}
                        className="text-cadet-blue-500 hover:text-dodger-blue-500 flex items-center space-x-2"
                        variant="secondary"
                      >
                        <DownloadIcon className="h-4 w-4 fill-current" />
                        <span>Export</span>
                      </PopoverButton>
                      <StyledPopoverPanel align="center" className="!w-[235px]">
                        {({ close }) => (
                          <>
                            {canExportCsv ? (
                              <PopoverItem
                                onClick={() => {
                                  close();
                                  exportData();
                                }}
                                className="group flex items-center"
                              >
                                {isDashboard ? `Export to spreadsheet (.xlsx)` : `Export to file (.csv)`}
                              </PopoverItem>
                            ) : null}
                            {canExportJson ? (
                              <PopoverItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  close();
                                  downloadResults();
                                }}
                                className="group flex items-center space-x-2"
                              >
                                <span>Export as JSON</span>
                              </PopoverItem>
                            ) : null}
                          </>
                        )}
                      </StyledPopoverPanel>
                    </>
                  )}
                </Popover>
              </div>
            ) : null}

            {/* <div className="flex items-center"> */}
            {/*   {[ */}
            {/*     { label: 'Day', value: TIME_UNITS.DAY, className: '!rounded-none !rounded-l' }, */}
            {/*     { label: 'Week', value: TIME_UNITS.WEEK, className: '!rounded-none border-l border-r' }, */}
            {/*     { label: 'Month', value: TIME_UNITS.MONTH, className: '!rounded-none !rounded-r' }, */}
            {/*   ].map(({ label, value, className }) => ( */}
            {/*     <Button */}
            {/*       key={value} */}
            {/*       variant={timeUnitOverride === value ? undefined : 'secondary'} */}
            {/*       className={className} */}
            {/*       onClick={() => setTimeUnitOverride((current) => (current === value ? null : value))} */}
            {/*     > */}
            {/*       {label} */}
            {/*     </Button> */}
            {/*   ))} */}
            {/* </div> */}

            <DateRangePicker
              lowerBoundary={pickerLowerBoundary}
              upperBoundary={pickerUpperBoundary}
              startDate={startDate ? startDate * 1000 : undefined}
              endDate={endDate ? endDate * 1000 : undefined}
              special={predefinedRange}
              setDateRange={chooseDateRange}
              setCommonDateRange={chooseDateRange}
              showCommonRanges={true}
              size="lg"
              maxRange={365}
              timezone={selectedSource?.currentTimezone ?? TIMEZONES.UTC}
              showTimeZone={true}
            />
          </div>
        </div>

        {showGaLoader ? (
          <div className="bg-white w-full h-[416px] p-10 rounded flex items-center justify-center flex-col shadow-md">
            <div className={pollState.status === 'stillPolling' ? 'max-w-[408px] text-center' : null}>
              <LoaderBalloon
                text={
                  pollState.status === 'polling'
                    ? 'Importing data from Google Analytics...'
                    : pollState.status === 'stillPolling'
                      ? 'Hang tight, this can take up to 5 to 10 minutes...'
                      : pollState.status === 'transition'
                        ? 'Creating your dashboard...'
                        : 'Loading...'
                }
              />
            </div>
          </div>
        ) : (
          <>
            {hasData || view || isLoading ? (
              view ? (
                <DetailView
                  isLoading={isLoading}
                  analytics={webAnalyticsData}
                  overTimeData={overTimeData}
                  conversionsData={conversionsData}
                  overTimeLoading={overTimeLoading}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                />
              ) : (
                <ListView
                  lowerBoundary={pickerLowerBoundary}
                  upperBoundary={pickerUpperBoundary}
                  isLoading={isLoading}
                  analytics={webAnalyticsData}
                  conversions={conversionsData}
                  timeUnitOverride={timeUnitOverride}
                />
              )
            ) : (
              <NoMatchesWall
                text={
                  <span>
                    data with the selected date range.
                    <br />
                    Try changing the date range
                  </span>
                }
              />
            )}

            {!view && hasData ? (
              <Button
                variant="cancel"
                className="justify-end w-full space-x-1.5 items-center mt-3.75 flex"
                onClick={() => scrollToLater(0)}
              >
                <ArrowIcon className="-rotate-90 fill-current w-2.5 h-2.5" />
                <span>Back to top</span>
              </Button>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
